import { DownOutlined } from '@ant-design/icons';
import basicStyle, { smallTimeOptions } from 'assets/styles/constants';
import SuperFetch from 'library/helpers/superFetch';
import WithHyperLink from 'library/hoc/withHyperLink';
import { useManagePermittedRoles } from 'library/hooks';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { teamsActions } from 'reduxStore/teams/slice';
import { Loader, ProfileImageComponent, Notification } from 'zComponents/atoms';
import { TeamActionsDropdown } from 'zComponents/molecules';
import { Button, Card, Col, Dropdown, Flex, Row, Space, Typography } from 'zui/Antd';
import { MetaContainer } from './TS.styles';

const { Text, Title } = Typography;

const IndividualTeamSummary = ({ data }) => {
	const { rowStyle, colStyle } = basicStyle;
	const dispatch = useDispatch();

	const { data: teamsData } = useSelector((state) => state.Teams);
	const { data: sitesData } = useSelector((state) => state.Sites);

	const [loadings, setLoading] = useState(true);
	const [teamList, setTeamList] = useState([]);

	useEffect(() => {
		setLoading(true);
		if (data && Object.keys(data).length > 0) {
			setTeamList(teamsData);
			setLoading(false);
		}
	}, [data, teamsData]);

	const isPermittedUpdateTeam = useManagePermittedRoles('updateTeam', {
		orgID: [data.orgID],
		siteID: [data.siteID],
		teamID: [data.teamID]
	});

	const onEdit = (value, field) => {
		if (field === 'name' && data.name !== value) {
			if (!/^[a-zA-Z0-9 -/]+$/.test(value)) {
				Notification('error', 'Team name can only contain letters, numbers and spaces.');
			} else if (value.length > 40) {
				Notification('error', 'Team name can only be 40 characters long.');
			} else {
				SuperFetch.patch('/teams/' + data.teamID, { name: value })
					.then((res) => {
						if (res) {
							if (res.statusCode === 200) {
								dispatch(teamsActions.loadTeams());
								Notification('success', 'Team Name Updated.');
							}
						}
						return res;
					})
					.catch(() => {
						Notification('error', 'Update Failed, Please try again.');
					});
			}
		}

		if (field === 'description' && data.description !== value) {
			if (!/^[a-zA-Z0-9 \n.;/,-]+$/.test(value)) {
				Notification('error', 'Team description can only contain letters, numbers, ,/;. and spaces.');
			} else if (value.length > 320) {
				Notification('error', 'Team description can only be 320 characters long.');
			} else {
				SuperFetch.patch('/teams/' + data.teamID, { description: value })
					.then((res) => {
						if (res?.statusCode === 200) {
							dispatch(teamsActions.loadTeams());
							Notification('success', 'Team Description Updated.');
						} else {
							throw new Error('Update Failed, Please try again.');
						}
					})
					.catch(() => {
						Notification('error', 'Update Failed, Please try again.');
					});
			}
		}
		return;
	};

	// HOC
	const BaseLink = WithHyperLink((a) => {
		return <p>{a.children}</p>;
	});

	const NameWithLink = (props) => {
		return <BaseLink {...props} />;
	};

	const SiteWithLink = (props) => {
		return <BaseLink {...props} />;
	};

	return loadings ? (
		<Row style={rowStyle} gutter={0} justify='space-around'>
			<Col lg={24} md={24} sm={24} xs={24} style={colStyle}>
				<Loader />
			</Col>
		</Row>
	) : (
		<Card>
			<Row lg={12} md={12} sm={24} xs={24} style={rowStyle}>
				<Col span={3}>
					<ProfileImageComponent entityObject={data} size={130} entityName={'Team'} />
				</Col>
				<Col span={21}>
					<Row style={{ justifyContent: 'space-between' }}>
						<div style={{ flexGrow: 1 }}>
							<Title
								level={3}
								style={{
									margin: 0
								}}
								editable={isPermittedUpdateTeam}
								editableProps={{
									editable: {
										tooltip: 'click to edit Team name',
										icon: (
											<i
												className='material-icons-outlined'
												style={{ color: '#4d4f5c', fontSize: '20px' }}
											>
												mode_edit
											</i>
										),
										triggerType: ['text', 'icon'],
										onChange: (value) => onEdit(value, 'name')
									}
								}}
							>
								{data.name}
							</Title>
						</div>

						<Flex>
							{teamList.length > 0 ? (
								<Dropdown
									menu={{
										items: teamList.map((team) => {
											return {
												label: (
													<NameWithLink type='team' dataID={team.teamID}>
														{' '}
														{team.name}{' '}
													</NameWithLink>
												)
											};
										})
									}}
									trigger={['click']}
								>
									<Button style={{ marginRight: '10px' }}>
										Switch Team <DownOutlined />
									</Button>
								</Dropdown>
							) : null}
							<TeamActionsDropdown team={data} onIndividualTeam />
						</Flex>
					</Row>
					<Row gutter={[0, 7]}>
						<div className='infoContainer'>
							<Text
								editable={
									isPermittedUpdateTeam && {
										tooltip: 'Click to edit Description',
										onChange: (value) => onEdit(value, 'description'),
										autoSize: { maxRows: 5 },
										icon: (
											<i
												className='material-icons-outlined'
												style={{ color: '#4d4f5c', fontSize: '15px' }}
											>
												mode_edit
											</i>
										),
										triggerType: ['text', 'icon']
									}
								}
								className='infoProp'
							>
								{data.description}
							</Text>
							<div className='infoDiv'>
								<label className='infoLabel'>Site: </label>
								<p className='infoProp'>
									{sitesData.find((site) => site.siteID === data.siteID) ? (
										<SiteWithLink type='site' dataID={data.siteID}>
											<p
												key={data.siteID}
												style={{
													cursor: 'pointer'
												}}
											>
												{sitesData.find((site) => site.siteID === data.siteID).name}
											</p>
										</SiteWithLink>
									) : (
										'N/A'
									)}
								</p>
							</div>
						</div>
					</Row>
				</Col>
			</Row>
			<MetaContainer>
				<Row className='metaContainer'>
					<Space wrap className='dateContainer'>
						<p
							className='metaField'
							title={new Date(data.modifiedDate).toLocaleString('en-AU', smallTimeOptions)}
						>
							<span className='metaLabel'>Updated: </span>
							{new Date(data.modifiedDate).toLocaleString('en-AU', smallTimeOptions)}
						</p>
						<p
							className='metaField'
							title={new Date(data.createdDate).toLocaleString('en-AU', smallTimeOptions)}
						>
							<span className='metaLabel'>Created: </span>
							{new Date(data.createdDate).toLocaleString('en-AU', smallTimeOptions)}
						</p>
					</Space>
				</Row>
			</MetaContainer>
		</Card>
	);
};

export default IndividualTeamSummary;
